<script setup lang="ts">
/**
 * A location the customer shared (LINE / Messenger pin): place name, address, coordinates, open in Google Maps,
 * copy the coordinates (e.g. for the order's Lalamove pin). No map tiles are loaded — nothing leaves the browser
 * until someone opens the map.
 */
import { computed, ref } from 'vue'
import type { Attachment } from '../../api/types'
import { copyText } from '../../lib/chatLink'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ place: Attachment }>()

const coords = computed(() => `${(props.place.lat ?? 0).toFixed(6)}, ${(props.place.lng ?? 0).toFixed(6)}`)
const mapUrl = computed(
  () => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${props.place.lat},${props.place.lng}`)}`,
)
const title = computed(() => props.place.name?.trim() || 'ตำแหน่งที่ลูกค้าแชร์')
const copied = ref(false)
async function copy(): Promise<void> {
  copied.value = await copyText(coords.value)
  if (copied.value) window.setTimeout(() => (copied.value = false), 1800)
}
</script>

<template>
  <div class="loc" role="group" :aria-label="`ตำแหน่งที่ตั้ง: ${title}`">
    <div class="loc-head">
      <span class="loc-pin" aria-hidden="true"><Icon name="mapPin" /></span>
      <div class="loc-txt">
        <b class="loc-t">{{ title }}</b>
        <span v-if="place.address" class="loc-a">{{ place.address }}</span>
        <span class="loc-c mono">{{ coords }}</span>
      </div>
    </div>
    <div class="loc-act">
      <a class="loc-b" :href="mapUrl" target="_blank" rel="noopener noreferrer"><Icon name="external" />เปิดใน Google Maps</a>
      <button type="button" class="loc-b" @click="copy"><Icon :name="copied ? 'check' : 'copy'" />{{ copied ? 'คัดลอกแล้ว' : 'คัดลอกพิกัด' }}</button>
    </div>
  </div>
</template>

<style scoped>
.loc {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 220px;
  max-width: 320px;
}
.loc-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.loc-pin {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f4ee;
  color: var(--ac);
}
.loc-pin :deep(.ic) {
  width: 18px;
  height: 18px;
}
.loc-txt {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}
.loc-t {
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
}
.loc-a {
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.loc-c {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.loc-act {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.loc-b {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  line-height: 20px;
  font-weight: 600;
  text-decoration: none;
}
.loc-b:hover {
  background: var(--acs);
}
.loc-b :deep(.ic) {
  width: 14px;
  height: 14px;
}
</style>
